import { IdleAnimation, IdleAnimationStage, waitForAnimationEnd } from './idle-animation';
import './toy-parade.css';

// --- silhouettes ----------------------------------------------------------
// Flat, single-colour (fill: currentColor) shadow puppets. Each viewBox is
// sized to its own proportions; the CSS normalises them onto the footer line.

// Chunky He-Man-style hero with a raised sword — the scout who leads the troupe.
const LEADER_SVG = `
<svg viewBox="0 0 32 44" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="24" y="1" width="1.8" height="15.5" rx="0.6"/>
  <rect x="21.8" y="15.2" width="5.6" height="1.7" rx="0.6"/>
  <circle cx="13.4" cy="6" r="3.7"/>
  <path d="M6.6 11 H20.4 L18.1 22 H9 Z"/>
  <path d="M18.4 11.4 L24.6 14.8 L23.4 17.2 L17.2 14.4 Z"/>
  <path d="M8.4 11.8 L5.4 20 L7.7 20.9 L10.4 12.8 Z"/>
  <path d="M9 22 L7.3 33.6 L10.7 33.6 L11.7 24 Z"/>
  <path d="M13.8 22 L17.4 33 L20.6 31.9 L17.7 23 Z"/>
  <rect x="6.4" y="32.6" width="4.9" height="3.5" rx="0.9"/>
  <rect x="17" y="31.2" width="5.2" height="3.5" rx="0.9" transform="rotate(12 19.6 33)"/>
</svg>`;

// Round-eared teddy-bear plush — soft, stubby and squat.
const PLUSH_SVG = `
<svg viewBox="0 0 30 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <circle cx="8.5" cy="8" r="3.4"/>
  <circle cx="21.5" cy="8" r="3.4"/>
  <circle cx="15" cy="13.5" r="7"/>
  <path d="M6.5 22 H23.5 L22 34 H8 Z"/>
  <circle cx="6.5" cy="21.5" r="3.2"/>
  <circle cx="23.5" cy="21.5" r="3.2"/>
  <rect x="7.5" y="33" width="5.2" height="4" rx="2"/>
  <rect x="17.3" y="33" width="5.2" height="4" rx="2"/>
</svg>`;

// Low-slung Hot-Wheels-style toy car with a rear spoiler and fat wheels.
// Shapes are authored facing left, then mirrored so the car faces the parade's
// direction of travel (left-to-right) with its spoiler trailing at the rear.
const CAR_SVG = `
<svg viewBox="0 0 44 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(44,0) scale(-1,1)">
    <path d="M2 17 L2 13 L11 13 L16 7 L30 7 L33 13 L42 13.5 L42 17 Z"/>
    <path d="M17 7.6 L28.5 7.6 L30.6 12.4 L14.4 12.4 Z"/>
    <rect x="38" y="6.5" width="4.5" height="2" rx="0.8"/>
    <rect x="40.4" y="6.5" width="2" height="6.5" rx="0.8"/>
    <circle cx="12" cy="18" r="5"/>
    <circle cx="32" cy="18" r="5"/>
  </g>
</svg>`;

// Boxy retro tin robot with antenna, square head and stompy feet.
const ROBOT_SVG = `
<svg viewBox="0 0 30 44" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="14" y="1.5" width="1.4" height="4.5" rx="0.5"/>
  <circle cx="14.7" cy="1.5" r="1.6"/>
  <rect x="9.5" y="6" width="11" height="9" rx="1.6"/>
  <rect x="7" y="16" width="16" height="13" rx="1.8"/>
  <rect x="3.4" y="17" width="3.2" height="9" rx="1.4"/>
  <rect x="23.4" y="17" width="3.2" height="9" rx="1.4"/>
  <rect x="9.5" y="30" width="4.4" height="9" rx="1"/>
  <rect x="16.1" y="30" width="4.4" height="9" rx="1"/>
  <rect x="8.4" y="38.5" width="6" height="3" rx="1"/>
  <rect x="15.6" y="38.5" width="6" height="3" rx="1"/>
</svg>`;

// Big long-necked apatosaurus — the towering grand finale. Head and snout point
// right (the parade's direction of travel); the tail trails off to the left.
const DINO_SVG = `
<svg viewBox="0 0 56 44" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M16 23 C10 22 5 26 1 33 L2.5 35 C6 30 11 28 17 27 Z"/>
  <ellipse cx="26" cy="25" rx="14" ry="8.5"/>
  <path d="M33 21 C33 12 38 6 46 4 L50 5 C51 6 51 8 49 9 L46 9 C42 12 40 16 40 22 Z"/>
  <ellipse cx="48" cy="6" rx="4.6" ry="3.4"/>
  <rect x="50.5" y="5" width="4" height="2.6" rx="1"/>
  <rect x="15" y="27" width="5" height="17" rx="1.4"/>
  <rect x="21" y="28" width="5" height="16" rx="1.4"/>
  <rect x="28" y="28" width="5" height="16" rx="1.4"/>
  <rect x="34" y="27" width="5" height="17" rx="1.4"/>
</svg>`;
// -------------------------------------------------------------------------

/**
 * The classic "toy parade": a He-Man-style scout tiptoes in first, pausing to
 * "check that nobody is around", then waves the rest of the troupe (a plush, a
 * Hot-Wheels-style car, a toy robot and a towering apatosaurus) in behind it on
 * staggered CSS animation-delays.
 *
 * This is one entry in the {@link IdleAnimationDirector} registry. It only owns
 * its choreography: it spawns actors, lets CSS keyframes drive the crossing, and
 * resolves once the last actor finishes (or the pass is aborted), removing every
 * node it created.
 */
export class ToyParadeAnimation implements IdleAnimation {
  readonly name = 'toy-parade';
  // The parade "walks the line" just above the footer, so it uses the footer strip stage.
  readonly stage: IdleAnimationStage = 'footer';

  async play(stage: HTMLElement, signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return;
    }

    const fragment = document.createDocumentFragment();
    const actors = ToyParadeAnimation.CAST.map((member) => {
      const actor = document.createElement('div');
      actor.className = `toy-parade-actor toy-parade-actor--${member.role}`;
      actor.style.animationDelay = `${member.delay}ms`;
      actor.innerHTML = `<div class="toy-parade-actor__body">${member.svg}</div>`;
      fragment.appendChild(actor);
      return actor;
    });

    stage.appendChild(fragment);

    // The last follower has the largest delay, so its crossing finishes last —
    // await its horizontal crossing (not the walk-cycle bob) to end the pass.
    const last = actors[actors.length - 1];
    await waitForAnimationEnd(last, 'toy-parade-cross', signal);

    for (const actor of actors) {
      actor.remove();
    }
  }

  // The troupe, in marching order. The leader scouts ahead and the followers
  // trail in behind on staggered delays. Every actor is a flat currentColor
  // silhouette so it reads as a shadow puppet against the backdrop.
  private static readonly CAST: ReadonlyArray<{
    role: string;
    delay: number;
    svg: string;
  }> = [
    { role: 'leader', delay: 0, svg: LEADER_SVG },
    { role: 'plush', delay: 2600, svg: PLUSH_SVG },
    { role: 'car', delay: 3400, svg: CAR_SVG },
    { role: 'robot', delay: 4200, svg: ROBOT_SVG },
    { role: 'dino', delay: 5000, svg: DINO_SVG }
  ];
}
